import React, { useState, useRef, useEffect, useCallback } from 'react';

const CircularPositionSelector = ({
  totalPoints: TOTAL_POINTS = 250,
  posicionActual,
  ejecutadas,
  onMoveRequest,
  bloqueado
}) => {
  const svgRef = useRef(null);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(null);
  const velocityRef = useRef(0); 
  const isInertiaRef = useRef(false);
  
  // Refs para cálculo de gestos
  const lastTouchTimeRef = useRef(0);
  const lastTouchPosRef = useRef(0);
  const gestureStartPosRef = useRef(0);
  const interactionModeRef = useRef(null); // 'POINTING', 'SPINNING', null
  const autoScrollSpeedRef = useRef(0); // Velocidad de auto-scroll por mantener en borde

  // viewCenterIndex es el "índice virtual" centrado
  const [viewCenterIndex, setViewCenterIndex] = useState(posicionActual || 0);
  const [isDragging, setIsDragging] = useState(false);
  const [tempSelection, setTempSelection] = useState(null);

  const VISIBLE_RANGE = 15;
  const RADIUS = 800; 
  const CENTER_X = 160;
  const CENTER_Y = 860;
  
  // Zonas
  const SCROLL_THRESHOLD = 110; // Pixeles para activar scroll por borde

  useEffect(() => {
    if (!isDragging && !isInertiaRef.current) {
      setViewCenterIndex(posicionActual);
    }
  }, [posicionActual, isDragging]);

  const normalizeIndex = (idx) => {
    let res = idx % TOTAL_POINTS;
    if (res < 0) res += TOTAL_POINTS;
    return res;
  };

  const getPointPosition = (index, currentViewCenter) => {
    let diff = index - currentViewCenter;
    if (diff > TOTAL_POINTS / 2) diff -= TOTAL_POINTS;
    if (diff < -TOTAL_POINTS / 2) diff += TOTAL_POINTS;

    const ANGLE_PER_INDEX = 1.6; 
    const angleDeg = diff * ANGLE_PER_INDEX;
    const angleRad = (angleDeg - 90) * (Math.PI / 180);

    return {
      x: CENTER_X + RADIUS * Math.cos(angleRad),
      y: CENTER_Y + RADIUS * Math.sin(angleRad),
      angleDeg: angleDeg,
      isVisible: Math.abs(angleDeg) < 30
    };
  };

  const getIndexFromX = (xOffset, currentViewCenter) => {
    // CLAMPING: Limitamos xOffset para que no rompa el Math.asin si se sale del radio
    const clampedX = Math.max(-RADIUS, Math.min(RADIUS, xOffset));
    
    const deltaRad = Math.asin(clampedX / RADIUS);
    const deltaDeg = deltaRad * (180 / Math.PI);
    const ANGLE_PER_INDEX = 1.6; 
    
    const indexDiff = deltaDeg / ANGLE_PER_INDEX;
    return currentViewCenter + indexDiff;
  };

  const getTouchXOffset = (e) => {
    if (!svgRef.current) return 0;
    const rect = svgRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    // Ajustar escala X por si el width no es 320 exacto
    const scaleX = 320 / rect.width;
    const relativeX = (clientX - rect.left) * scaleX;
    return relativeX - CENTER_X;
  };

  // --- FÍSICA E INERCIA ---
  const startPhysicsLoop = () => {
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    lastTimeRef.current = Date.now();
    
    const loop = () => {
      const now = Date.now();
      // Limitar dt para evitar saltos gigantes si el navegador se cuelga un momento
      const dt = Math.min((now - lastTimeRef.current) / 1000, 0.032); 
      lastTimeRef.current = now;

      let shouldContinue = false;

      // 1. GESTIÓN DE INERCIA (Frenado)
      if (isInertiaRef.current) {
        const friction = 2.0; 
        if (Math.abs(velocityRef.current) > 0.5) {
          velocityRef.current *= (1 - friction * dt);
          setViewCenterIndex(prev => normalizeIndex(prev - velocityRef.current * dt));
          shouldContinue = true;
        } else {
          isInertiaRef.current = false;
          velocityRef.current = 0;
          // Snap final
          setViewCenterIndex(prev => {
              const snap = normalizeIndex(Math.round(prev));
              onMoveRequest(snap);
              return snap;
          });
        }
      } 
      
      // 2. GESTIÓN DE AUTO-SCROLL (Bordes en Pointing)
      if (Math.abs(autoScrollSpeedRef.current) > 0 && isDragging && interactionModeRef.current === 'POINTING') {
          setViewCenterIndex(prev => normalizeIndex(prev + autoScrollSpeedRef.current * dt));
          
          // Actualizar selección bajo el dedo mientras el fondo se mueve
          // Necesitamos la posición X actual del dedo (última conocida)
          const currentX = lastTouchPosRef.current;
          const rawIndex = getIndexFromX(currentX, viewCenterIndex); // Nota: viewCenterIndex aquí es el del render anterior en closure, pero React state update encolado lo arreglará visualmente. 
          // Para precisión en loop, deberíamos usar el valor calculado prev arriba, pero no tenemos acceso fácil.
          // Solución: Dejar que el re-render actualice tempSelection via efecto o confiar en que el movimiento es suave.
          // Mejor: No actualizamos tempSelection dentro del loop para no spammear renders,
          // o usamos un ref para viewCenterIndex si quisiéramos lógica perfecta sin re-render.
          // Por simplicidad visual, el movimiento de viewCenterIndex desencadenará un render,
          // pero tempSelection se quedará "atrás" un frame. Es aceptable.
          
          shouldContinue = true;
      }
      
      if (shouldContinue) {
        animationRef.current = requestAnimationFrame(loop);
      } else {
        animationRef.current = null;
      }
    };
    
    animationRef.current = requestAnimationFrame(loop);
  };

  // --- GESTORES DE EVENTOS ---

  const getTouchYOffset = (e) => {
    if (!svgRef.current) return 0;
    const rect = svgRef.current.getBoundingClientRect();
    const clientY = e.touches ? e.touches[0].clientY : e.clientY; 
    // El viewBox ahora empieza en -100 y tiene un height total de 280
    const scaleY = 280 / rect.height;
    const relativeY = (clientY - rect.top) * scaleY;
    
    // Si el viewBox empieza en -100, el borde superior del rect es y=-100 interno.
    return relativeY - 100; 
  };

  const handleStart = (e) => {
    if (bloqueado) return;
    
    // Captura de puntero para no perder el rastro al salir del área
    if (e.target.setPointerCapture && e.pointerId) {
      e.target.setPointerCapture(e.pointerId);
    }
    
    // Resetear estados
    isInertiaRef.current = false;
    interactionModeRef.current = null; 
    velocityRef.current = 0;
    autoScrollSpeedRef.current = 0;
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    
    setIsDragging(true);
    
    const xOffset = getTouchXOffset(e);
    const yOffset = getTouchYOffset(e);
    
    lastTouchPosRef.current = xOffset;
    lastTouchTimeRef.current = Date.now();
    gestureStartPosRef.current = xOffset;

    // DECISIÓN DE MODO POR ZONA Y (Vertical)
    // Zona de puntos (arriba) vs Zona de control (abajo)
    // El SVG tiene height 180. La barra de control empieza en 100 visualmente.
    // Damos un margen generoso: Arriba de 110 es APUNTAR, Abajo de 110 es GIRAR.
    if (yOffset > 110) {
        interactionModeRef.current = 'SPINNING';
        setTempSelection(null);
    } else {
        // MODO APUNTAR: Abarca toda la parte superior
        interactionModeRef.current = 'POINTING';
        
        // Clampeamos xOffset al ancho visual del SVG (±160) para la selección
        const selectionX = Math.max(-160, Math.min(160, xOffset));
        const rawIndex = getIndexFromX(selectionX, viewCenterIndex);
        setTempSelection(normalizeIndex(Math.round(rawIndex)));
    }
  };

  const handleMove = (e) => {
    if (!isDragging || bloqueado) return;
    const xOffset = getTouchXOffset(e);
    const now = Date.now();
    const dt = (now - lastTouchTimeRef.current) / 1000;
    const absX = Math.abs(xOffset);

    // Detección de velocidad instantánea
    if (dt > 0.001) {
       const dx = xOffset - lastTouchPosRef.current;
       const pxPerSec = dx / dt;
       
       // Guardar para inercia al soltar
       // Convertimos px/s a indices/s (aprox 14px/indice)
       velocityRef.current = pxPerSec / 14;
    }

    // LÓGICA BASADA EN MODO FIJO
    if (interactionModeRef.current === 'SPINNING') {
        // MODO GIRO / SCROLL (Zona Inferior)
        // La rueda sigue al dedo (1:1 drag)
        const dx = xOffset - lastTouchPosRef.current;
        const dIndex = dx / 14; 
        
        setViewCenterIndex(prev => normalizeIndex(prev - dIndex));
        setTempSelection(null);

    } else {
        // MODO APUNTAR (POINTING) (Zona Superior)
        // La rueda se queda QUIETA en el centro...
        // PERO si nos acercamos al borde, activamos autoScrollSpeedRef para el loop
        
        // SCROLL_THRESHOLD es 110. Ancho total es 320 (centro 160). Borde es a 160px.
        if (absX > SCROLL_THRESHOLD) {
            const penetration = absX - SCROLL_THRESHOLD;
            const dir = xOffset > 0 ? 1 : -1;
            // Velocidad suave de exploración (max 15 indices/seg)
            autoScrollSpeedRef.current = (penetration / 40) * 15 * dir; 
            
            // Iniciar loop si no corre
            if (!animationRef.current) startPhysicsLoop();
        } else {
            autoScrollSpeedRef.current = 0;
        }
        
        // Calculamos qué está bajo el dedo AHORA, limitado a los bordes visuales (±160)
        const selectionX = Math.max(-160, Math.min(160, xOffset));
        const rawIndex = getIndexFromX(selectionX, viewCenterIndex);
        setTempSelection(normalizeIndex(Math.round(rawIndex)));
    }

    lastTouchPosRef.current = xOffset;
    lastTouchTimeRef.current = now;
  };

  const handleEnd = (e) => {
    setIsDragging(false);
    autoScrollSpeedRef.current = 0; // Detener auto-scroll al soltar
    
    const totalDist = Math.abs(lastTouchPosRef.current - gestureStartPosRef.current);
    const isSmallMovement = totalDist < 5; // Umbral de píxeles para ignorar movimiento accidental

    // Si estábamos girando (por velocidad o borde), aplicamos inercia
    if (interactionModeRef.current === 'SPINNING') {
        // Solo activamos inercia si el movimiento no es accidental y tiene suficiente velocidad
        if (!isSmallMovement && Math.abs(velocityRef.current) > 5) {
            isInertiaRef.current = true;
            startPhysicsLoop();
        } else {
            // Si el movimiento fue muy pequeño, volvemos al número actual
            if (isSmallMovement) {
                setViewCenterIndex(posicionActual);
            } else {
                // Freno en seco
                setViewCenterIndex(prev => {
                    const snap = Math.round(prev);
                    onMoveRequest(normalizeIndex(snap));
                    return normalizeIndex(snap);
                });
            }
        }
    } else {
        // MODO APUNTAR: Confirmar selección
        if (tempSelection !== null) {
            onMoveRequest(tempSelection);
            // Centrar la vista en la selección
            setViewCenterIndex(tempSelection);
        }
    }
    
    setTempSelection(null);
    interactionModeRef.current = null;
  };

  // Generar puntos visibles
  const visiblePoints = [];
  const startIdx = Math.floor(viewCenterIndex) - VISIBLE_RANGE;
  const endIdx = Math.floor(viewCenterIndex) + VISIBLE_RANGE;

  for (let i = startIdx; i <= endIdx; i++) {
    const idx = normalizeIndex(i);
    const pos = getPointPosition(i, viewCenterIndex);
    if (pos.isVisible) {
      visiblePoints.push({ idx, ...pos });
    }
  }

  return (
    <div className="flex flex-col items-center justify-center h-full p-2 select-none overflow-hidden">
      <div className="text-[10px] text-slate-300 uppercase tracking-widest mb-1 font-bold h-4">
        {isDragging ? (interactionModeRef.current === 'SPINNING' ? 'Girando...' : 'Apuntando...') : (isInertiaRef.current ? 'Inercia...' : 'Selecciona arriba / Gira abajo')}
      </div>
      
      <svg
        ref={svgRef}
        viewBox="0 -100 320 280"
        className={`w-full max-w-[320px] h-auto touch-none select-none ${bloqueado ? 'opacity-30' : 'cursor-pointer'}`}
        onPointerDown={handleStart}
        onPointerMove={handleMove}
        onPointerUp={handleEnd}
        onPointerLeave={handleEnd}
        // Fallback para touch events si pointer events no son soportados (raro hoy en dia)
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      >
        <defs>
          <linearGradient id="fadeEdge" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0f172a" stopOpacity="1" />
            <stop offset="15%" stopColor="#0f172a" stopOpacity="0" />
            <stop offset="85%" stopColor="#0f172a" stopOpacity="0" />
            <stop offset="100%" stopColor="#0f172a" stopOpacity="1" />
          </linearGradient>
        </defs>

        {/* Guía de arco */}
        <path 
          d={`M 0 60 Q 160 30 320 60`} 
          fill="none" 
          stroke="#334155" 
          strokeWidth="1" 
          strokeDasharray="4 4"
          opacity="0.3"
        />

        {/* Indicadores de zona de scroll */}
        <path d="M 20 50 L 10 60 L 20 70" stroke="#475569" fill="none" strokeWidth="2" opacity="0.5" />
        <path d="M 300 50 L 310 60 L 300 70" stroke="#475569" fill="none" strokeWidth="2" opacity="0.5" />

        {/* Puntos visibles */}
        {visiblePoints.map((p) => {
          const isCurrent = p.idx === posicionActual;
          
          // La "posición señalada" es la que se resalta visualmente
          const indicatedPos = interactionModeRef.current === 'POINTING' && tempSelection !== null 
              ? tempSelection 
              : (isDragging || isInertiaRef.current ? normalizeIndex(Math.round(viewCenterIndex)) : posicionActual);

          const isProtagonist = p.idx === indicatedPos;
          const isExecuted = ejecutadas.includes(p.idx);
          
          const distFromCenter = Math.abs(p.angleDeg);
          // Curva de tamaño más agresiva para destacar al protagonista
          let sizeFactor = Math.max(0.5, 1 - (distFromCenter / 25)); 
          
          if (isProtagonist) {
              sizeFactor = 1.3; // Extra grande para el foco
          } else if (distFromCenter < 5) {
              sizeFactor = 0.9; // Vecinos cercanos
          } else {
              sizeFactor *= 0.8; // Lejanos más pequeños
          }

          const radius = (isProtagonist ? 5 : 2.5) * sizeFactor;
          
          // Offset vertical para levantar el número seleccionado sobre el dedo (solo en modo apuntar)
          // YA NO SE USA: Ahora mostramos el número en un HUD central fijo
          
          return (
            <g key={`pt-${p.idx}-${p.angleDeg}`} transform={`translate(${p.x}, ${p.y})`}>
              <circle
                r={radius}
                fill={isProtagonist ? '#fbbf24' : (isExecuted ? '#22c55e' : (isCurrent ? '#60a5fa' : '#64748b'))}
                className="transition-colors duration-200"
                opacity={isProtagonist ? 1 : 0.7}
              />
              {/* Número pequeño solo para contexto en vecinos, NO en el protagonista (que sale en el HUD) */}
              {(distFromCenter < 15 && !isProtagonist) && (
                <text
                  y={-15}
                  textAnchor="middle"
                  fill={isCurrent && !isProtagonist ? '#60a5fa' : '#94a3b8'}
                  fontSize={10 * sizeFactor}
                  fontWeight="normal"
                  className="font-mono select-none"
                >
                  {p.idx}
                </text>
              )}
            </g>
          );
        })}

        {/* HUD CENTRAL DE SELECCIÓN */}
        {/* Muestra el número seleccionado (o actual) siempre en el centro superior */}
        <g transform="translate(160, -25)">
           <text 
              textAnchor="middle" 
              fill="#fbbf24" 
              fontSize="48" 
              fontWeight="900" 
              className="font-mono select-none"
              style={{ textShadow: '0 4px 12px rgba(0,0,0,0.5)' }}
           >
              {interactionModeRef.current === 'POINTING' && tempSelection !== null 
                  ? tempSelection 
                  : (isDragging || isInertiaRef.current ? normalizeIndex(Math.round(viewCenterIndex)) : posicionActual)}
           </text>
           <text 
              y="20"
              textAnchor="middle" 
              fill="#fbbf24" 
              fontSize="10" 
              fontWeight="bold" 
              className="uppercase tracking-widest opacity-80"
           >
              {interactionModeRef.current === 'POINTING' ? 'SELECCIONANDO' : 'POSICIÓN'}
           </text>
        </g>

        {/* Zona de Control Inferior (Visual) */}
        <g transform="translate(0, 110)">
           <rect x="0" y="0" width="320" height="70" fill="transparent" />
           {/* Pista de deslizamiento */}
           <rect x="40" y="30" width="240" height="4" rx="2" fill="#334155" opacity="0.3" />
           {/* Indicador de "Thumb" virtual que sigue al centro relativo (opcional, o solo arrows) */}
           <path d="M 20 32 L 10 32 L 15 25" fill="#475569" opacity="0.5" />
           <path d="M 300 32 L 310 32 L 305 25" fill="#475569" opacity="0.5" />
           <text x="160" y="55" textAnchor="middle" fill="#475569" fontSize="9" className="uppercase tracking-widest opacity-50 font-bold">
              Deslizar para Girar
           </text>
        </g>

        {/* Cursor central estático para referencia */}
        <path d={`M ${CENTER_X} 20 L ${CENTER_X} 50`} stroke="#fbbf24" strokeWidth="2" opacity="0.5" />

      </svg>
      
      {/* Leyenda */}
      <div className="flex gap-4 mt-2 text-[9px] uppercase tracking-tighter font-semibold justify-center">
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 bg-green-500 rounded-full shadow-sm shadow-green-900/50"></div>Clavado</div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 bg-slate-400 rounded-full"></div>Pendiente</div>
        <div className="flex items-center gap-1.5"><div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>Actual</div>
      </div>
    </div>
  );
};

export default CircularPositionSelector;
